/**
 * Typed client for Hyperliquid `POST /info` (read-only).
 *
 * - URL comes from the EnvConfig (config/env.ts); never hard-code hosts here.
 * - Retries 429 / 5xx / network errors with exponential backoff + jitter (honours Retry-After). This is the only
 *   retry layer for /info: at most 1 + maxRetries attempts. TanStack Query retries are off (main.tsx, #1809).
 * - With a `cooldown` (infoCooldown.ts, the web app always has one) a 429 pauses every /info request, not just this one.
 * - Identical in-flight requests (same canonical body) share one fetch.
 * - AbortSignal per call. A shared fetch is only aborted once every caller has aborted.
 */
import type { EnvConfig } from '../config/networks';
import { canonicalJson } from './canonical';
import type { InfoCooldown } from './infoCooldown';
import type {
  AllMidsResponse,
  BasicOrder,
  Candle,
  ClearinghouseState,
  FrontendOrder,
  HistoricalOrder,
  L2Book,
  OutcomeMetaResponse,
  SpotClearinghouseState,
  SpotMetaAndAssetCtxsResponse,
  SpotMetaResponse,
  UserFill,
} from './types';

export class HlHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
    readonly request: unknown,
  ) {
    super(`HL /info ${status}: ${body.slice(0, 200)}`);
    this.name = 'HlHttpError';
  }
}

export interface InfoClientOptions {
  fetch?: typeof fetch;
  /** Retries after the first attempt (default 2). */
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Injectable for tests. Must reject with an AbortError when `signal` aborts. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  random?: () => number;
  /** Shared 429 gate. Without one a 429 only delays its own retry (the backend's clients have their own budget). */
  cooldown?: InfoCooldown;
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Retries for this call (default: the client's maxRetries). Ignored when the call joins an identical in-flight one. */
  maxRetries?: number;
}

export interface InfoClient {
  readonly infoUrl: string;
  /** Raw typed POST; prefer the named methods. */
  request<T>(body: Record<string, unknown>, opts?: RequestOptions): Promise<T>;
  outcomeMeta(opts?: RequestOptions): Promise<OutcomeMetaResponse>;
  spotMeta(opts?: RequestOptions): Promise<SpotMetaResponse>;
  /** [spotMeta, asset ctxs]; ctxs include both side coins of every outcome ("#<enc>") with 24h volume (#874). */
  spotMetaAndAssetCtxs(opts?: RequestOptions): Promise<SpotMetaAndAssetCtxsResponse>;
  /**
   * OHLCV candles of one coin (#874 volume). At most the 5,000 most recent candles exist. Hyperliquid answers
   * `[]` for a listed coin with no trades and HTTP 500 with body `null` for a coin it does not know (e.g. an outcome
   * that has just settled).
   */
  candleSnapshot(
    coin: string,
    interval: string,
    startTime: number,
    endTime?: number,
    opts?: RequestOptions,
  ): Promise<Candle[] | null>;
  allMids(opts?: RequestOptions): Promise<AllMidsResponse>;
  spotClearinghouseState(user: string, opts?: RequestOptions): Promise<SpotClearinghouseState>;
  /** Main perp dex by default (dex ""). */
  clearinghouseState(user: string, dex?: string, opts?: RequestOptions): Promise<ClearinghouseState>;
  frontendOpenOrders(user: string, dex?: string, opts?: RequestOptions): Promise<FrontendOrder[]>;
  openOrders(user: string, dex?: string, opts?: RequestOptions): Promise<BasicOrder[]>;
  /** Up to 2000 most recent fills, newest first. */
  userFills(user: string, aggregateByTime?: boolean, opts?: RequestOptions): Promise<UserFill[]>;
  /** Up to 2000 most recent order status changes. */
  historicalOrders(user: string, opts?: RequestOptions): Promise<HistoricalOrder[]>;
  l2Book(coin: string, params?: { nSigFigs?: number; mantissa?: number }, opts?: RequestOptions): Promise<L2Book>;
}

interface InFlight {
  promise: Promise<unknown>;
  controller: AbortController;
  waiters: number;
}

export function abortError(): Error {
  // DOMException is available in browsers, jsdom and Node >= 17.
  return new DOMException('The operation was aborted.', 'AbortError');
}

export function isAbortError(e: unknown): boolean {
  // Not `instanceof Error`: DOMException from another realm (jsdom, iframes) fails that check.
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError';
}

export const defaultSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) return reject(abortError());
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

export function retryAfterMs(res: Response): number | undefined {
  const h = res.headers.get('retry-after');
  if (!h) return undefined;
  const secs = Number(h);
  if (Number.isFinite(secs)) return Math.max(0, secs * 1000);
  const at = Date.parse(h);
  return Number.isFinite(at) ? Math.max(0, at - Date.now()) : undefined;
}

export function createInfoClient(env: Pick<EnvConfig, 'infoUrl'>, options: InfoClientOptions = {}): InfoClient {
  const baseFetch = options.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
  const { cooldown } = options;
  const doFetch = cooldown ? cooldown.wrapFetch(baseFetch) : baseFetch;
  const maxRetries = options.maxRetries ?? 2;
  const baseDelayMs = options.baseDelayMs ?? 300;
  const maxDelayMs = options.maxDelayMs ?? 5_000;
  const sleep = options.sleep ?? defaultSleep;
  const random = options.random ?? Math.random;
  const inFlight = new Map<string, InFlight>();
  const url = env.infoUrl;

  async function run(body: Record<string, unknown>, signal: AbortSignal, retries = maxRetries): Promise<unknown> {
    const payload = JSON.stringify(body);
    for (let attempt = 0; ; attempt++) {
      let waitMs: number | undefined;
      try {
        const res = await doFetch(url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: payload,
          signal,
        });
        if (res.ok) return (await res.json()) as unknown;
        const text = await res.text().catch(() => '');
        const retryable = res.status === 429 || res.status >= 500;
        if (!retryable || attempt >= retries) throw new HlHttpError(res.status, text, body);
        // The next doFetch waits out the shared cooldown; a second delay here would only add to it.
        if (res.status === 429 && cooldown) continue;
        waitMs = retryAfterMs(res);
      } catch (e) {
        if (signal.aborted || isAbortError(e)) throw abortError();
        if (e instanceof HlHttpError) throw e;
        // Network-level failure (TypeError: Failed to fetch, ECONNRESET, bad JSON on a 200 is NOT retried).
        if (e instanceof SyntaxError || attempt >= retries) throw e;
      }
      const backoff = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
      const jittered = backoff / 2 + random() * (backoff / 2);
      await sleep(Math.min(maxDelayMs, Math.max(waitMs ?? 0, jittered)), signal);
    }
  }

  function request<T>(body: Record<string, unknown>, opts: RequestOptions = {}): Promise<T> {
    const { signal } = opts;
    if (signal?.aborted) return Promise.reject(abortError());
    const key = canonicalJson(body);
    let entry = inFlight.get(key);
    if (!entry) {
      const controller = new AbortController();
      const created: InFlight = { controller, waiters: 0, promise: Promise.resolve() };
      created.promise = run(body, controller.signal, opts.maxRetries).finally(() => {
        if (inFlight.get(key) === created) inFlight.delete(key);
      });
      // Avoid unhandled-rejection noise when every waiter has gone away.
      created.promise.catch(() => {});
      inFlight.set(key, created);
      entry = created;
    }
    const shared = entry;
    shared.waiters++;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      shared.waiters--;
      if (shared.waiters === 0) {
        shared.controller.abort();
        // A new identical request must start a fresh fetch, not join the aborted one.
        if (inFlight.get(key) === shared) inFlight.delete(key);
      }
    };
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => {
        release();
        reject(abortError());
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      shared.promise.then(
        (v) => {
          signal?.removeEventListener('abort', onAbort);
          released = true;
          resolve(v as T);
        },
        (e: unknown) => {
          signal?.removeEventListener('abort', onAbort);
          released = true;
          reject(e);
        },
      );
    });
  }

  return {
    infoUrl: url,
    request,
    outcomeMeta: (o) => request({ type: 'outcomeMeta' }, o),
    spotMeta: (o) => request({ type: 'spotMeta' }, o),
    spotMetaAndAssetCtxs: (o) => request({ type: 'spotMetaAndAssetCtxs' }, o),
    candleSnapshot: (coin, interval, startTime, endTime, o) =>
      request(
        { type: 'candleSnapshot', req: { coin, interval, startTime, ...(endTime !== undefined ? { endTime } : {}) } },
        o,
      ),
    allMids: (o) => request({ type: 'allMids' }, o),
    spotClearinghouseState: (user, o) => request({ type: 'spotClearinghouseState', user }, o),
    clearinghouseState: (user, dex = '', o) => request({ type: 'clearinghouseState', user, dex }, o),
    frontendOpenOrders: (user, dex = '', o) => request({ type: 'frontendOpenOrders', user, dex }, o),
    openOrders: (user, dex = '', o) => request({ type: 'openOrders', user, dex }, o),
    userFills: (user, aggregateByTime, o) =>
      request({ type: 'userFills', user, ...(aggregateByTime ? { aggregateByTime: true } : {}) }, o),
    historicalOrders: (user, o) => request({ type: 'historicalOrders', user }, o),
    l2Book: (coin, params, o) => request({ type: 'l2Book', coin, ...params }, o),
  };
}
